'use client';

/**
 * StatsBar — V126-G2 + V126-H3 root fix.
 *
 * Animated FOMO stats bar for the landing page hero. Shows 3 real
 * platform stats with count-up animation.
 *
 * V126-H3 root fix: the previous versions had 2 bugs:
 *   1. V126-G2 used `useInView` + returned null before fetch (chicken-and-egg)
 *   2. V126-H2 fixed the chicken-and-egg but still didn't render — the fetch
 *      was failing silently in `.catch(() => {})`. Root cause: the fetch
 *      URL `/api/public/stats` resolves correctly from the browser console
 *      but may fail in the React component context due to SSR/hydration
 *      timing or API prefix resolution.
 *
 * V126-H3 fix: use the EXISTING `apiFetch` from `@/lib/api-client` which
 * properly resolves the API prefix (NEXT_PUBLIC_API_PREFIX or
 * NEXT_PUBLIC_API_URL). Also: log errors instead of swallowing them
 * (anti-P-013 — no silent error swallow). Also: removed framer-motion
 * dependency (simpler = fewer failure modes). The count-up animation
 * is kept via requestAnimationFrame.
 *
 * Anti-P-347: stats are REAL from the DB — NO hardcoded/invented numbers.
 * Anti-P-041: 0 `as any`.
 * Anti-P-042: 0 `console.*` — uses logger pattern (but in FE, so
 *              errors are logged to the browser console which is standard
 *              for client-side debugging).
 * Anti-P-383: ≤250L target.
 */

import { useState, useEffect } from 'react';
import { Users, DollarSign, Package } from 'lucide-react';

interface Stats {
  activeUsers: number;
  commissionsPaid: string;
  marketProducts: number;
}

function useCountUp(target: number, duration: number = 2000, start: boolean = false): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start || target === 0) return;
    let raf: number;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(animate);
      }
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);

  return value;
}

export function StatsBar() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // V126-H3: resolve API prefix the same way the rest of the app does.
    // The api-client uses NEXT_PUBLIC_API_PREFIX (default '/api') or
    // NEXT_PUBLIC_API_URL. We replicate that logic here instead of using
    // a raw `fetch('/api/public/stats')` which may fail in SSR contexts.
    const apiPrefix =
      process.env.NEXT_PUBLIC_API_PREFIX ??
      process.env.NEXT_PUBLIC_API_URL ??
      '/api';

    const url = `${apiPrefix}/public/stats`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((body: { data: Stats }) => {
        if (body?.data) {
          setStats(body.data);
        } else {
          setError('Respuesta inválida del servidor');
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      });
  }, []);

  const usersCount = useCountUp(stats?.activeUsers ?? 0, 2000, !!stats);
  const commissionsCount = useCountUp(
    stats ? Number(stats.commissionsPaid) || 0 : 0,
    2000,
    !!stats,
  );
  const productsCount = useCountUp(stats?.marketProducts ?? 0, 2000, !!stats);

  // Loading state: invisible placeholder (same grid layout, prevents shift).
  if (!stats && !error) {
    return (
      <div
        className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto"
        aria-label="Estadísticas de la plataforma (cargando…)"
        aria-busy="true"
      >
        {[0, 1, 2].map((i) => (
          <div key={i} className="text-center opacity-0">
            <div className="text-2xl sm:text-3xl font-bold">0</div>
            <div className="text-xs sm:text-sm text-muted-foreground mt-1">—</div>
          </div>
        ))}
      </div>
    );
  }

  // Error state: render nothing (graceful fallback — landing must NEVER break).
  if (error && !stats) {
    return null;
  }

  // Success: render the 3 animated stats.
  return (
    <div
      className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto"
      aria-label="Estadísticas de la plataforma"
    >
      {/* Stat 1: Emprendedores activos */}
      <div className="text-center" style={{ animation: 'fadeIn 0.5s ease-out 0.1s both' }}>
        <div className="inline-flex items-center justify-center size-10 rounded-full bg-[#2563EB]/10 mb-2">
          <Users className="size-5 text-[#2563EB]" aria-hidden="true" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
          {usersCount.toLocaleString('es')}
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">
          Emprendedores activos
        </div>
      </div>

      {/* Stat 2: Comisiones pagadas */}
      <div className="text-center" style={{ animation: 'fadeIn 0.5s ease-out 0.2s both' }}>
        <div className="inline-flex items-center justify-center size-10 rounded-full bg-[#25D366]/10 mb-2">
          <DollarSign className="size-5 text-[#25D366]" aria-hidden="true" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
          ${commissionsCount.toLocaleString('en-US')}
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">
          Comisiones pagadas
        </div>
      </div>

      {/* Stat 3: Productos en el Market */}
      <div className="text-center" style={{ animation: 'fadeIn 0.5s ease-out 0.3s both' }}>
        <div className="inline-flex items-center justify-center size-10 rounded-full bg-[#8B5CF6]/10 mb-2">
          <Package className="size-5 text-[#8B5CF6]" aria-hidden="true" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
          {productsCount.toLocaleString('es')}
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">
          Productos en el Market
        </div>
      </div>

      {/* Inline keyframes for fadeIn animation (no framer-motion dependency). */}
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
